import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Button, Form, Input, Row, App, Typography} from 'antd';
import {
    EyeInvisibleOutlined,
    EyeOutlined, InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import './Login.css';
import {MainImage} from "../../components/image/MainImage";
import {themeLocations} from "../../theme/themeLocations";
import MainLayout from "../../components/layout/MainLayout";
import {showApiError} from "../../utility/error/error-handler";
import {getDriverRouteById} from "../../components/routes/driverRoutes";
import {useTranslation} from "react-i18next";
import {
    getSessionData, storeSessionData,
} from "../../utility/global-util";
import HeaderBanner from "../../components/layout/HeaderBanner";
import {changePassword} from "../../api/security";
import useOnlineStatus from "../../utility/hooks/useOnlineStatus";

const { Title} = Typography;



// The root  component.
const ChangePassword = () => {

    const { t } = useTranslation();

    const { isOnline } = useOnlineStatus();

    const { message, notification } = App.useApp();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const [form] = Form.useForm();

    const [updateProgress, setUpdateProgress] = useState(100);  //start 100 - button shows loading if less than 100
    const [changeMode, setChangeMode] = useState(null);

    const [textVisible0, setTextVisible0] = useState(false);
    const [textVisible1, setTextVisible1] = useState(false);
    const [textVisible2, setTextVisible2] = useState(false);
    const suffixIcon0 = textVisible0
        ? (<EyeInvisibleOutlined onClick={() => setTextVisible0(false)} />)
        : (<EyeOutlined onClick={() => setTextVisible0(true)} />);
    const fieldType0 = textVisible0 ? 'text' : 'password';
    const suffixIcon1 = textVisible1
        ? (<EyeInvisibleOutlined onClick={() => setTextVisible1(false)} />)
        : (<EyeOutlined onClick={() => setTextVisible1(true)} />);
    const fieldType1 = textVisible1 ? 'text' : 'password';
    const suffixIcon2 = textVisible2
        ? (<EyeInvisibleOutlined onClick={() => setTextVisible2(false)} />)
        : (<EyeOutlined onClick={() => setTextVisible2(true)} />);
    const fieldType2 = textVisible2 ? 'text' : 'password';

    const onFinish = (values) => {
        if(isOnline)
        {
            // Check passwords match
            if (values.password !== values.repeat_password) {
                notification.open({
                    icon: <WarningOutlined style={{color: 'var(--dp-status-colour-warning)'}}/>,
                    description: t('error.passwords_not_same'),
                    duration: null,
                });
            }

            // Update password
            else {
                changePassword(values.old_password, values.repeat_password, setUpdateProgress)
                    .then(res => {
                        // No data returned - message success and navigate to login page
                        message.success(t('message.successfully_submitted', {0: t('field.password')}));
                        notification.destroy();
                        navigate(getDriverRouteById('home'));
                    })
                    .catch(err => {
                        notification.destroy();
                        const replaceCommaWithNewLine = (msg) => { return msg.replaceAll(',', "<br />") };
                        showApiError(null, err, notification, [], replaceCommaWithNewLine);
                    });
            }
        }
        else
        {
            notification.open({
                icon: <WarningOutlined style={{color: 'var(--dp-status-colour-warning)'}}/>,
                description: t('error.offline'),
                duration: null,
            });
        }
    };
    const onFinishFailed = (errorInfo) => {
        console.log('Form Failed:', errorInfo);
    };

    const cancelAction = () => {
        if (changeMode === 'expired') {
            // Logout and return to login
            storeSessionData('loginHash', '');
            navigate(getDriverRouteById('start'));
        } else if (changeMode === 'change') {
            navigate(getDriverRouteById('contact-details'));
        }
    }

    useEffect(() => {
        const changeMode = searchParams ? searchParams.get("mode") : null;
        if (!changeMode) {
            console.error('change mode not passed in!');
            navigate(getDriverRouteById('missing'));
        } else {
            setChangeMode(changeMode);
            if (changeMode === 'expired') {
                if (getSessionData('keepExpXXX')) {
                    form.setFieldsValue({"old_password": getSessionData('keepExpXXX')});
                    storeSessionData('keepExpXXX', null);
                }
                notification.destroy();
                notification.open({
                    icon: <InfoCircleOutlined style={{color: 'var(--dp-status-colour-info)'}}/>,
                    description: t('message.password_expired'),
                    duration: null,
                });
            }
        }
    }, [searchParams, navigate, notification, t, form]);

    return (
        <MainLayout showHeader={false} showTabMenu={changeMode ? changeMode === 'change' : true}
                    showSubHeader={true} startHigher="220px"
                    subHeaderContent={<HeaderBanner />} subHeaderClass="dp-full-no-vehicle">
            <Row type="flex" align="middle" justify="center">
                <div className="login-content">
                    <Row type="flex" justify="center" className="login-logo">
                        <MainImage imageSrc={themeLocations.MAIN_LOGO} />
                    </Row>
                    <Row type="flex" justify="center" className="login-logo" style={{ margin: '5px auto 5px auto' }}>
                        <Title level={3}>{t('title.change_password')}</Title>
                    </Row>
                    <Row>
                        <Form
                            form={form}
                            className="login-form"
                            name="set-pwd-dp"
                            onFinish={onFinish}
                            onFinishFailed={onFinishFailed}
                            autoComplete="off"
                            layout="vertical"
                        >
                            <Form.Item
                                name="old_password"
                                label={t('field.password_current')}
                                rules={[
                                    { required: true, message: t('error.no_value_named', {0: t('field.password_current')}) },
                                ]}
                                style={{ marginTop: '-10px' }}
                            >
                                <Input
                                    type={fieldType0}
                                    placeholder={t('action.enter_value')}
                                    maxLength={80}
                                    suffix={suffixIcon0}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label={t('field.password_new')}
                                rules={[
                                    { required: true, message: t('error.no_value_named', {0: t('field.password_new')}) },
                                ]}
                                style={{ marginTop: '-10px' }}
                            >
                                <Input
                                    type={fieldType1}
                                    placeholder={t('action.enter_value')}
                                    maxLength={80}
                                    suffix={suffixIcon1}
                                />
                            </Form.Item>

                            <Form.Item
                                name="repeat_password"
                                label={t('field.password_confirm')}
                                rules={[
                                    { required: true, message: t('error.no_value_named', {0: t('field.password_confirm')}) },
                                ]}
                                style={{ marginTop: '-10px' }}
                            >
                                <Input
                                    type={fieldType2}
                                    placeholder={t('action.enter_value')}
                                    maxLength={80}
                                    suffix={suffixIcon2}
                                />
                            </Form.Item>

                            <Row style={{ margin: '15px 0' }} justify="end">
                                <Button style={{ marginRight: '5px' }} onClick={() => cancelAction()}>
                                    {t('action.cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit" loading={updateProgress < 100}>
                                    {t('action.submit')}
                                </Button>
                            </Row>
                        </Form>
                    </Row>
                </div>
            </Row>
        </MainLayout>
    );
}

export default ChangePassword;
